// One series, one hue (the sequential blue) as a light fill: the sparkline
// gives a shape under the value, it is not meant to be read.
const TREND_LINE = '#2a78d6'
const TREND_FILL = 'rgba(42, 120, 214, 0.08)'

/** Décimale à la française (8.5 → « 8,5 »). */
export const pct = (n) => String(n ?? 0).replace('.', ',')

/** Formatage FR d'un compteur (1 284 → « 1 284 »). */
export const compact = (n) =>
    n == null
        ? '—'
        : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(n)

/** Prix en dollars, tel que facturé (0.2212 → « 0,22 $ »). */
export const dollars = (n) =>
    n == null ? '—' : `${Number(n).toFixed(2).replace('.', ',')} $`

/**
 * Full-width sparkline: a thin line over a very light fill, no axis, no
 * marker. It stretches to the tile's width (`preserveAspectRatio="none"`):
 * a shape, not a value.
 */
export const Sparkline = ({ points, height = 28 }) => {
    const data = (points || []).filter((v) => v != null)
    if (data.length < 2) return null

    const w = 100
    const h = 100
    const max = Math.max(...data)
    const min = Math.min(...data)
    const span = max - min || 1
    const x = (i) => (i / (data.length - 1)) * w
    const y = (v) => h - 6 - ((v - min) / span) * (h - 12)

    const line = data.map((v, i) => `${x(i)},${y(v)}`).join(' ')
    const area = `0,${h} ${line} ${w},${h}`

    return (
        <svg
            width="100%"
            height={height}
            viewBox={`0 0 ${w} ${h}`}
            preserveAspectRatio="none"
            className="block"
            aria-hidden="true"
        >
            <polygon points={area} fill={TREND_FILL} />
            <polyline
                points={line}
                fill="none"
                stroke={TREND_LINE}
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
                strokeLinejoin="round"
                strokeLinecap="round"
            />
        </svg>
    )
}

/**
 * Variation vs la période précédente de même durée. Rien n'est affiché tant que
 * la période précédente n'a pas de données — un delta contre du vide mentirait.
 * `unit="pts"` pour les taux (un « +7 % » sur un pourcentage est ambigu).
 */
export const Delta = ({ current, previous, unit = '%' }) => {
    if (current == null || previous == null || previous === 0) return null

    const diff =
        unit === 'pts'
            ? current - previous
            : (100 * (current - previous)) / previous
    const up = diff >= 0
    const value = `${Math.abs(diff).toFixed(1).replace('.', ',')} ${unit === 'pts' ? 'pts' : '%'}`

    return (
        <span
            className={`inline-flex items-center gap-0.5 text-xs font-medium ${
                up ? 'text-green-600' : 'text-red-500'
            }`}
        >
            {up ? '↑' : '↓'} {value}
        </span>
    )
}

/**
 * The KPI strip: one block, one column per tile, separated by hairlines. No
 * card per figure — four boxes side by side is the same frame four times.
 */
export const StatTiles = ({ children }) => (
    <div className="grid grid-cols-2 divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200/70 bg-white @4xl:grid-cols-4 @4xl:divide-x @4xl:divide-y-0">
        {children}
    </div>
)

/**
 * One column of the strip: label · large light value · full-width sparkline
 * · delta and note in grey.
 */
export const StatTile = ({ label, value, unit, note, points, delta }) => (
    <div className="flex flex-col px-6 py-5">
        <div className="text-sm text-gray-500">{label}</div>
        <div className="mt-2 flex items-baseline gap-1.5">
            <span className="text-3xl font-semibold tracking-tight text-gray-900 tabular-nums">
                {value}
            </span>
            {unit && <span className="text-sm text-gray-400">{unit}</span>}
        </div>
        {points && (
            <div className="mt-3">
                <Sparkline points={points} />
            </div>
        )}
        {(delta || note) && (
            <div className="mt-3 flex flex-wrap items-baseline gap-x-2 text-xs text-gray-400">
                {delta}
                {delta && <span>vs période précédente</span>}
                {note && <span className={delta ? 'basis-full' : ''}>{note}</span>}
            </div>
        )}
    </div>
)

/* ------------------------------------------------------- graphes empilés */

/**
 * Réglages d'axes communs aux barres empilées des deux écrans : mêmes gris,
 * mêmes graduations. Défini une fois, sinon les deux pages divergent au
 * premier ajustement.
 */
export const barScales = (yTicks = {}) => ({
    x: {
        stacked: true,
        grid: { display: false },
        border: { display: false },
        ticks: { color: '#9ca3af', font: { size: 10 }, maxRotation: 0, autoSkipPadding: 12 },
    },
    y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: '#f3f4f6' },
        border: { display: false },
        ticks: { color: '#9ca3af', font: { size: 10 }, maxTicksLimit: 5, ...yTicks },
    },
})

export const dataset = (label, data, color) => ({
    label,
    data,
    backgroundColor: color,
    borderColor: '#fff',
    borderWidth: 1,
    borderRadius: 3,
    maxBarThickness: 24,
})
